import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Icon,
  Spinner,
  Button,
  Avatar,
  Heading,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FaTrophy,
  FaMedal,
  FaAward,
  FaChevronRight,
  FaStar,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getReviews } from '../services/reviewService';
import { getBusinessImages } from '../services/imageService';
import { logger } from '../utils/logger';

const MotionBox = motion(Box);

/**
 * ReviewTotalsWidget - Displays top businesses by total review count
 * Features a podium-style layout for top 3 businesses
 */
const ReviewTotalsWidget = () => {
  const navigate = useNavigate();
  const [reviewsData, setReviewsData] = useState(null);
  const [businessImages, setBusinessImages] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtitleColor = useColorModeValue('gray.500', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        // Fetch reviews data
        const reviewsResponse = await getReviews();
        
        if (mounted) {
          setReviewsData(reviewsResponse);
        }

        // Fetch business images
        const images = await getBusinessImages().catch(err => {
          logger.log('Images not available:', err.message);
          return [];
        });

        if (mounted && images) {
          // Create maps_uri -> image URL mapping
          const imageMap = {};
          images.forEach(img => {
            if (img.maps_uri && img.high_res_image_url_fetched) {
              imageMap[img.maps_uri] = img.high_res_image_url_fetched;
            }
          });
          setBusinessImages(imageMap);
        }
      } catch (error) {
        logger.error('Error fetching Review Totals data:', error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  // Get sorted businesses by total review count
  const sortedByTotal = useMemo(() => {
    if (!reviewsData || reviewsData.length === 0) return [];
    return [...reviewsData]
      .sort((a, b) => b.rating_count - a.rating_count);
  }, [reviewsData]);

  // Get top 3 for podium
  const top3Businesses = useMemo(() => {
    return sortedByTotal.slice(0, 3);
  }, [sortedByTotal]);

  // Calculate market total
  const totalMarketReviews = useMemo(() => {
    return sortedByTotal.reduce((sum, b) => sum + (b.rating_count || 0), 0);
  }, [sortedByTotal]);

  const handleViewDetails = () => {
    navigate('/dashboards/total-reviews');
  };

  // Loading state
  if (isLoading) {
    return (
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        p={6}
        boxShadow="sm"
      >
        <Flex align="center" justify="center" minH="300px">
          <VStack spacing={3}>
            <Spinner size="lg" color="blue.500" thickness="3px" />
            <Text color={subtitleColor} fontSize="sm">Loading review data...</Text>
          </VStack>
        </Flex>
      </Box>
    );
  }

  // No data state
  if (!reviewsData || sortedByTotal.length === 0) {
    return (
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        p={6}
        boxShadow="sm"
      >
        <VStack spacing={3} py={8}>
          <Icon as={FaStar} boxSize={10} color="gray.300" />
          <Text color={subtitleColor}>No review data available</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <MotionBox
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      bg={cardBg}
      borderRadius="2xl"
      borderWidth="1px"
      borderColor={borderColor}
      p={6}
      boxShadow="sm"
    >
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={3}>
          <Icon as={FaStar} color="blue.500" boxSize={6} />
          <Heading size="md" textTransform="uppercase" fontStyle="italic" fontSize="1.3em">
            Review Leaders
          </Heading>
        </HStack>
        <Button
          size="sm"
          colorScheme="blue"
          variant="ghost"
          rightIcon={<FaChevronRight />}
          onClick={handleViewDetails}
        >
          View Full Dashboard
        </Button>
      </Flex>

      {/* Market Summary */}
      <HStack spacing={4} mb={6} justify="center">
        <VStack spacing={0}>
          <Text fontSize="2xl" fontWeight="bold" color="blue.600">
            {totalMarketReviews.toLocaleString()}
          </Text>
          <Text fontSize="xs" color={subtitleColor} textTransform="uppercase">
            Market Total
          </Text>
        </VStack>
        <Box h="40px" w="1px" bg={borderColor} />
        <VStack spacing={0}>
          <Text fontSize="2xl" fontWeight="bold" color="blue.600">
            {sortedByTotal.length}
          </Text>
          <Text fontSize="xs" color={subtitleColor} textTransform="uppercase">
            Businesses
          </Text>
        </VStack>
      </HStack>

      {/* Top 3 Podium */}
      {top3Businesses.length >= 3 && (
        <Box mb={4}>
          <Flex justify="center" align="flex-end" gap={4} w="full">
            {/* Silver - Second Place */}
            <Box
              bg={cardBg}
              borderRadius="xl"
              textAlign="center"
              position="relative"
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{ 
                transform: 'translateY(-4px)', 
                shadow: 'xl',
              }}
              overflow="hidden"
              flex="1"
              maxW="200px"
              boxShadow="lg"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <Box h="3px" bgGradient="linear(to-r, gray.300, gray.500, gray.300)" />
              <VStack p={4} spacing={3}>
                <Flex
                  position="absolute"
                  top={3}
                  left={3}
                  minW="28px"
                  h="28px"
                  px={2}
                  borderRadius="full"
                  bgGradient="linear(to-br, gray.300, gray.500)"
                  justify="center"
                  align="center"
                  shadow="md"
                >
                  <Text
                    color="white"
                    fontWeight="800"
                    fontSize="md"
                    lineHeight="1"
                  >
                    2
                  </Text>
                </Flex>
                
                <Icon as={FaMedal} color="gray.400" boxSize={5} />
                
                <Box p="2px" borderRadius="full" bgGradient="linear(to-br, gray.300, gray.500)">
                  <Avatar 
                    name={top3Businesses[1]?.name} 
                    src={businessImages[top3Businesses[1]?.maps_uri] || ''} 
                    bg="gray.100" 
                    boxSize="50px"
                    borderWidth="2px"
                    borderColor={cardBg}
                  />
                </Box>
                
                <VStack spacing={0}>
                  <Tooltip label={top3Businesses[1]?.name} placement="top" hasArrow openDelay={300}>
                    <Text fontSize="sm" fontWeight="bold" noOfLines={1} color={textColor}>
                      {top3Businesses[1]?.name}
                    </Text>
                  </Tooltip>
                  <Text 
                    fontWeight="extrabold" 
                    fontSize="xl" 
                    bgGradient="linear(to-r, gray.600, gray.800)"
                    bgClip="text"
                  >
                    {top3Businesses[1]?.rating_count?.toLocaleString() ?? 0}
                  </Text>
                  <Text fontSize="2xs" color={subtitleColor} fontWeight="medium" textTransform="uppercase" letterSpacing="wider">
                    reviews
                  </Text>
                </VStack>
                
                <Box w="full" px={2}>
                  <Box h="4px" bg="gray.100" borderRadius="full" overflow="hidden">
                    <Box
                      h="full"
                      w={`${(top3Businesses[1]?.rating_count / (top3Businesses[0]?.rating_count || 1)) * 100}%`}
                      bgGradient="linear(to-r, gray.400, gray.500)"
                      borderRadius="full"
                      transition="width 0.5s ease-out"
                    />
                  </Box>
                </Box>
              </VStack>
            </Box>

            {/* Gold - First Place */}
            <Box
              bg={cardBg}
              borderRadius="xl"
              textAlign="center"
              position="relative"
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{ 
                transform: 'translateY(-8px) scale(1.02)', 
                shadow: '0 20px 40px -12px rgba(251, 191, 36, 0.4)',
              }}
              overflow="hidden"
              flex="1.1"
              maxW="220px"
              boxShadow="0 15px 30px -10px rgba(251, 191, 36, 0.3)"
              zIndex={2}
              mb={4}
              borderWidth="1px"
              borderColor="yellow.200"
            >
              <Box
                h="4px"
                bgGradient="linear(to-r, yellow.300, yellow.400, orange.400, yellow.400, yellow.300)"
                backgroundSize="200% 100%"
                animation="shimmer 2s linear infinite"
                sx={{
                  '@keyframes shimmer': {
                    '0%': { backgroundPosition: '200% 0' },
                    '100%': { backgroundPosition: '-200% 0' },
                  },
                }}
              />
              <VStack p={5} spacing={3}>
                <Flex
                  position="absolute"
                  top={3}
                  left={3}
                  minW="32px"
                  h="32px"
                  px={2}
                  borderRadius="full"
                  bgGradient="linear(to-br, yellow.300, orange.400)"
                  justify="center"
                  align="center"
                  shadow="lg"
                >
                  <Text
                    color="white"
                    fontWeight="800"
                    fontSize="lg"
                    lineHeight="1"
                  >
                    1
                  </Text>
                </Flex>
                
                <Icon as={FaTrophy} color="yellow.400" boxSize={7} filter="drop-shadow(0 2px 4px rgba(251, 191, 36, 0.5))" />
                
                <Box p="3px" borderRadius="full" bgGradient="linear(to-br, yellow.300, orange.400, yellow.500)" shadow="lg">
                  <Avatar 
                    name={top3Businesses[0]?.name} 
                    src={businessImages[top3Businesses[0]?.maps_uri] || ''} 
                    bg="yellow.50" 
                    boxSize="65px"
                    borderWidth="3px"
                    borderColor={cardBg}
                  />
                </Box>
                
                <VStack spacing={0}>
                  <Tooltip label={top3Businesses[0]?.name} placement="top" hasArrow openDelay={300}>
                    <Text fontSize="md" fontWeight="bold" noOfLines={1} color={textColor}>
                      {top3Businesses[0]?.name}
                    </Text>
                  </Tooltip>
                  <Text 
                    fontWeight="extrabold" 
                    fontSize="2xl" 
                    bgGradient="linear(to-r, yellow.500, orange.500)"
                    bgClip="text"
                  >
                    {top3Businesses[0]?.rating_count?.toLocaleString() ?? 0}
                  </Text>
                  <Text fontSize="2xs" color={subtitleColor} fontWeight="medium" textTransform="uppercase" letterSpacing="wider">
                    reviews
                  </Text>
                </VStack>
                
                <Box w="full" px={2}>
                  <Box h="5px" bg="yellow.50" borderRadius="full" overflow="hidden">
                    <Box h="full" w="100%" bgGradient="linear(to-r, yellow.400, orange.400)" borderRadius="full" />
                  </Box>
                </Box>
              </VStack>
            </Box>

            {/* Bronze - Third Place */}
            <Box
              bg={cardBg}
              borderRadius="xl"
              textAlign="center"
              position="relative"
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{ 
                transform: 'translateY(-4px)', 
                shadow: 'xl',
              }}
              overflow="hidden"
              flex="1"
              maxW="200px"
              boxShadow="lg"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <Box h="3px" bgGradient="linear(to-r, orange.200, orange.400, orange.200)" />
              <VStack p={4} spacing={3}>
                <Flex
                  position="absolute"
                  top={3}
                  left={3}
                  minW="28px"
                  h="28px"
                  px={2}
                  borderRadius="full"
                  bgGradient="linear(to-br, orange.300, orange.500)"
                  justify="center"
                  align="center"
                  shadow="md"
                >
                  <Text
                    color="white"
                    fontWeight="800"
                    fontSize="md"
                    lineHeight="1"
                  >
                    3
                  </Text>
                </Flex>
                
                <Icon as={FaAward} color="orange.400" boxSize={5} />
                
                <Box p="2px" borderRadius="full" bgGradient="linear(to-br, orange.300, orange.500)">
                  <Avatar 
                    name={top3Businesses[2]?.name} 
                    src={businessImages[top3Businesses[2]?.maps_uri] || ''} 
                    bg="orange.50" 
                    boxSize="50px"
                    borderWidth="2px"
                    borderColor={cardBg}
                  />
                </Box>
                
                <VStack spacing={0}>
                  <Tooltip label={top3Businesses[2]?.name} placement="top" hasArrow openDelay={300}>
                    <Text fontSize="sm" fontWeight="bold" noOfLines={1} color={textColor}>
                      {top3Businesses[2]?.name}
                    </Text>
                  </Tooltip>
                  <Text 
                    fontWeight="extrabold" 
                    fontSize="xl" 
                    bgGradient="linear(to-r, orange.500, orange.600)"
                    bgClip="text"
                  >
                    {top3Businesses[2]?.rating_count?.toLocaleString() ?? 0}
                  </Text>
                  <Text fontSize="2xs" color={subtitleColor} fontWeight="medium" textTransform="uppercase" letterSpacing="wider">
                    reviews
                  </Text>
                </VStack>
                
                <Box w="full" px={2}>
                  <Box h="4px" bg="orange.50" borderRadius="full" overflow="hidden">
                    <Box
                      h="full"
                      w={`${(top3Businesses[2]?.rating_count / (top3Businesses[0]?.rating_count || 1)) * 100}%`}
                      bgGradient="linear(to-r, orange.300, orange.500)"
                      borderRadius="full"
                      transition="width 0.5s ease-out"
                    />
                  </Box>
                </Box>
              </VStack>
            </Box>
          </Flex>
        </Box>
      )}

      {/* Runner-ups list (4th and 5th place) */}
      {sortedByTotal.length > 3 && (
        <Box mt={4} pt={4} borderTopWidth="1px" borderColor={borderColor}>
          <Text fontSize="xs" fontWeight="semibold" color={subtitleColor} mb={3} textTransform="uppercase">
            Runner-ups
          </Text>
          <VStack spacing={2} align="stretch">
            {sortedByTotal.slice(3, 5).map((business, idx) => (
              <Flex
                key={business.maps_uri}
                align="center"
                justify="space-between"
                p={2}
                borderRadius="md"
                _hover={{ bg: hoverBg }}
              >
                <HStack spacing={3}>
                  <Flex
                    w="24px"
                    h="24px"
                    borderRadius="full"
                    bg="gray.100"
                    justify="center"
                    align="center"
                    fontWeight="bold"
                    fontSize="xs"
                    color="gray.600"
                  >
                    {idx + 4}
                  </Flex>
                  <Avatar 
                    name={business.name} 
                    src={businessImages[business.maps_uri] || ''} 
                    size="sm"
                  />
                  <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                    {business.name}
                  </Text>
                </HStack>
                <Text fontSize="sm" fontWeight="bold" color="blue.600">
                  {business.rating_count?.toLocaleString() ?? 0}
                </Text>
              </Flex>
            ))}
          </VStack>
        </Box>
      )}
    </MotionBox>
  );
};

export default ReviewTotalsWidget;
